import React, { type ForwardedRef, forwardRef, memo } from "react";
import clsx from "clsx";
import Navigation from "./Navigation";
import GradeSwitch from "./GradeSwitch";
import Container from "../../../../../shared-components/css-modules/lexus/atoms/Container";
import styles from "./Header.module.scss";
import { CarConfigStep } from "../../../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { useCarConfigSelector } from "../../../../redux/store";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import { CarConfigConfigureSectionsLbx } from "../../../../utils/types/SectionTypesLbx";
import { useAphNavigation } from "../../../../hooks/useAphNavigation";
import { useComponentRegistry } from "../../componentRegistry";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { CarConfigLabel } from "../../../../utils/labelUtils";
import { useAphBreakpoint } from "../../../../../shared-logic/hooks/useBreakpoint";
import SpecsNavigationContainer from "../../SpecsAndHighlights/SpecsNavigationContainer";
import type { SpecsNavigationItemType } from "../../CarConfigBase";

type HeaderType = {
    activeStep: CarConfigStep;
    useLbxJourney: boolean;
    navItems?: SpecsNavigationItemType[];
};

const Header = (props: HeaderType, ref: ForwardedRef<HTMLElement>): JSX.Element => {
    const { activeStep, useLbxJourney, navItems } = props;
    const hideSidebar = useCarConfigSelector((state) => state.carConfigSettings.hideSidebar);
    const modelName = useCarConfigSelector((state) => state.car.model.webtitleSectionName);
    const gradeName = useCarConfigSelector((state) => state.car.grade.webtitleSectionName);

    const handleClick = useAphNavigation();

    const BackButton = useComponentRegistry("HeaderBackButton");
    const Cta = useComponentRegistry("HeaderCta");
    const isMobile = useAphBreakpoint("down", "xl");

    return (
        <header className={styles.header} data-step={activeStep} ref={ref}>
            <Container className={styles["header-container"]}>
                <div className={styles["header-grid"]}>
                    <div className={styles.selector}>
                        <div className={styles.gradeswitch}>
                            {useLbxJourney ? (
                                <Button
                                    variant="clean"
                                    className={styles["lbx-grade-name"]}
                                    onClick={handleClick(
                                        CarConfigStep.Configure,
                                        CarConfigConfigureSectionsLbx.AtmosphereGrade,
                                    )}
                                >
                                    <Text tag="span" variant="apheleia-h4">
                                        {modelName} <span>{gradeName}</span>
                                    </Text>
                                    <Icon variant="chevron-down" sizing="sm" role="menu" aria-hidden="true" />
                                </Button>
                            ) : (
                                <GradeSwitch />
                            )}
                        </div>
                        <Navigation activeStep={activeStep} />
                    </div>
                    <div className={styles.cta}>
                        <Cta />
                    </div>
                </div>
            </Container>

            <div className={clsx(styles["back-bar"], hideSidebar && styles.full)}>
                <Container>
                    <BackButton />
                </Container>
            </div>
            {activeStep === CarConfigStep.SpecsAndHighlights && navItems && (
                <SpecsNavigationContainer hideNavigation={!isMobile} specsNavigationItems={navItems} />
            )}

            <Button
                variant="btn-default"
                href="#step-navigation"
                className={clsx(a11y["skip-to-content"], styles.skip)}
            >
                <CarConfigLabel label="carConfigA11yLinkSkipToAnchorsNavigation" />
            </Button>
        </header>
    );
};

export default memo(forwardRef(Header));
